<template>
  <div
    :id="'cart-carousel' + categoryid"
    class="cart-carousel slide"
    v-if="products && products.length"
  >
    <section class="embla" data-test="">
      <div
        class="embla__viewport"
        ref="emblaRef"
        @mouseenter="stopAutoplay"
        @mouseleave="startAutoplay"
      >
        <div class="embla__container">
          <div
            class="embla__slide product-item"
            v-for="product in products"
            :key="product.id"
            :data-product-id="product.id"
          >
            <div class="product-item-info row align-items-center">
              <ProductListImage
                :product="product"
                is-from-list
                :isProductListFromParent="true"
              />
              <div class="product-item-details col-sm-5">
                <h3 class="product-item-name">
                  <a :href="product.url">{{ product.name }}</a>
                </h3>
              </div>
              <div class="price-container col-sm-5">
                <span class="price-wrapper">
                  <span class="price">
                    <span class="amount">{{
                      formatPrice(getDefaultChoice(product).unitPrice)
                    }}</span>
                    <span class="currency">&euro;</span>
                  </span>
                </span>
                <template v-if="showaddtocart && getDefaultChoice(product)">
                  <AddToCart
                    :item="fillProductWithChoice(product)"
                    :btnlabel="addToCartLabel"
                  />
                </template>
                <a v-else :href="product.url">
                  <span class="goto-product">{{ $t("labels.more") }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="embla__controls">
        <div class="embla__buttons">
          <button
            @click="scrollPrev"
            class="embla__button embla__button--prev"
            type="button"
          >
            <svg class="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M520.646 355.66c13.805 13.793 13.805 36.208 0 50.001-13.804 13.785-36.238 13.785-50.034 0L266 201.22 61.391 405.66c-13.805 13.785-36.239 13.785-50.044 0-13.796-13.793-13.796-36.208 0-50.002 22.947-22.928 206.507-206.395 229.454-229.332a35.065 35.065 0 0 1 25.126-10.326c9.2 0 18.26 3.393 25.2 10.326 45.901 45.865 206.564 206.404 229.52 229.332Z"
              ></path>
            </svg>
          </button>

          <button
            @click="scrollNext"
            class="embla__button embla__button--next"
            type="button"
          >
            <svg class="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M11.354 176.34c-13.805-13.793-13.805-36.208 0-50.001 13.804-13.785 36.238-13.785 50.034 0L266 330.78l204.61-204.442c13.805-13.785 36.239-13.785 50.044 0 13.796 13.793 13.796 36.208 0 50.002a5994246.277 5994246.277 0 0 0-229.454 229.332 35.065 35.065 0 0 1-25.126 10.326c-9.2 0-18.26-3.393-25.2-10.326C194.973 359.808 34.31 199.269 11.354 176.34Z"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, watch } from "vue";
import emblaCarouselVue from "embla-carousel-vue";
import Autoplay from "embla-carousel-autoplay";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import ProductListImage from "@/components/productlist/subcomponents/Image.vue";
import AddToCart from "@/components/AddToCart.vue";
import Price from "@/services/price";

const props = defineProps({
  products: Array,
  categoryid: String,
  showaddtocart: Boolean,
  addToCartLabel: String,
  getDefaultChoice: Function,
  fillProductWithChoice: Function,
});

const options = {
  loop: true,
  align: "start",
  axis: "y",
  slidesToScroll: "auto",
};
const [emblaRef, emblaApi] = emblaCarouselVue(options, [
  Autoplay({ stopOnMouseEnter: true, delay: 5000 }),
  WheelGesturesPlugin(),
]);

const currentIndex = ref(0);

function scrollNext() {
  emblaApi?.value.scrollNext();
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function scrollPrev() {
  emblaApi?.value.scrollPrev();
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function scrollTo(index) {
  emblaApi?.value.scrollTo(index);
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function startAutoplay() {
  emblaApi?.value.plugins().autoplay.play();
}

function stopAutoplay() {
  emblaApi?.value.plugins().autoplay.stop();
  emblaApi?.value.plugins().autoplay.reset();
}

function formatPrice(price) {
  return Price.format(price);
}

onMounted(() => {
  if (emblaApi.value) {
    emblaApi.value.on("autoplay:select", (api) => {
      currentIndex.value = api.selectedScrollSnap();
    });
  }
});

watch(
  () => props.products,
  (newProducts) => {
    if (emblaApi.value && newProducts.length > 0) {
      emblaApi.value.reInit();
    }
  },
  { immediate: true },
);
</script>

<style>
.products-slider-cart .embla {
  max-width: 48rem;
  margin: auto;
  --slide-height: 20rem;
  --slide-spacing: 1.5rem;
  --slide-size: 33%;
  padding: 1rem 0;
}
.products-slider-cart .embla__viewport {
  overflow: hidden;
}
.products-slider-cart .embla__container {
  display: flex;
  touch-action: pan-x pinch-zoom;
  /*margin-top: calc(var(--slide-spacing) * 1);*/
  margin-top: -0.5rem;
  height: calc(var(--slide-spacing) + var(--slide-height) + 6rem);
  flex-direction: column;
}
.products-slider-cart .embla__slide {
  transform: translate3d(0, 0, 0);
  flex: 0 0 var(--slide-size);
  min-height: 0;
  padding-top: var(--slide-spacing);
}
.products-slider-cart .embla__slide__number {
  box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
  border-radius: 1.8rem;
  font-size: 4rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  user-select: none;
}
.products-slider-cart .embla__controls {
  display: grid;
  grid-template-columns: auto 1fr;
  justify-content: space-between;
  gap: 1.2rem;
  margin-top: 1rem;
}
.products-slider-cart .embla__buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  align-items: center;
}
.products-slider-cart .embla__button {
  -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  touch-action: manipulation;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
  width: 3.6rem;
  height: 3.6rem;
  z-index: 1;
  border-radius: 50%;
  color: var(--text-body);
  display: flex;
  align-items: center;
  justify-content: center;
}
.products-slider-cart .embla__button:disabled {
  color: var(--detail-high-contrast);
}
.products-slider-cart .embla__button__svg {
  width: 35%;
  height: 35%;
}

.products-slider-cart .product-item {
  position: relative;
  text-align: left;
  margin-bottom: 1rem;
  padding: 1.5rem;
  background: var(--senary-color);
  display: flex;
  align-items: center;
}

.products-slider-cart .product-item-name {
  display: block;
  word-wrap: break-word;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
  color: var(--primary-color);
  font-weight: bold !important;
  font-size: 1.15rem;
  overflow: hidden;
  text-transform: uppercase;
}

.products-slider-cart .price-container {
  text-align: center;
}

.products-slider-cart .price {
  text-rendering: optimizeLegibility;
  color: var(--font-color);
  font-size: 1.2rem;
  font-weight: bold !important;
}

.products-slider-cart .product-item-name {
  font-size: 1rem;
  margin: 0.5rem 0;
}

.products-slider-cart .add-to-cart-btn {
  font-size: 0.9rem;
  height: 36px;
  line-height: 36px;
}

.products-slider-cart .add-to-cart-btn:before {
  font-size: 0.9rem;
}

.products-slider-cart .product-image {
  max-width: 100%;
}

@media screen and (max-width: 575px) {
  .products-slider-cart .embla {
    --slide-size: 100%;
  }

  .products-slider-cart .product-item-name {
    text-align: center;
  }

  .products-slider-cart .product-image {
    width: 140px;
    margin: 0 auto 10px auto;
    display: block;
  }
}
</style>
